import { Formik } from 'formik';
import React, { useMemo } from 'react';
import { Button, Form } from 'react-bootstrap';

import { useProfileUpdate } from '../../../hooks';
import { Divider } from '../../BaseUI';
import { CheckBoxField, TextAreaField } from '../../FormFields';
import OpenToSchema, { DESC_MAX_CHARACTER_LIMIT } from './OpenToSchema';

interface OpenToProps {
  findingNewJob: boolean;
  findingNewJobDesc: string;
  isDisabled: boolean;
  hiring: boolean;
  hiringDesc: string;
  investingInStartups: boolean;
  investingInStartupsDesc: string;
  providingServices: boolean;
  providingServicesDesc: string;
  userId: number;
}

const OpenTo = ({
  isDisabled,
  userId,
  investingInStartups,
  investingInStartupsDesc,
  hiring,
  hiringDesc,
  findingNewJob,
  findingNewJobDesc,
  providingServices,
  providingServicesDesc,
}: OpenToProps) => {
  const initialState = useMemo(
    () => ({
      investingInStartups,
      investingInStartupsDesc: investingInStartupsDesc || '',
      hiring,
      hiringDesc: hiringDesc || '',
      findingNewJob,
      findingNewJobDesc: findingNewJobDesc || '',
      providingServices,
      providingServicesDesc: providingServicesDesc || '',
    }),
    [
      investingInStartups,
      investingInStartupsDesc,
      hiring,
      hiringDesc,
      findingNewJob,
      findingNewJobDesc,
      providingServices,
      providingServicesDesc,
    ]
  );

  const { saveProfile, isSavingProfile, showSave } = useProfileUpdate(userId);
  const handleSubmit = (values) => {
    const sanitizedParams = {
      investing_in_startups: values.investingInStartups,
      investing_in_startups_desc: values.investingInStartupsDesc,
      hiring: values.hiring,
      hiring_desc: values.hiringDesc,
      finding_new_job: values.findingNewJob,
      finding_new_job_desc: values.findingNewJobDesc,
      providing_services: values.providingServices,
      providing_services_desc: values.providingServicesDesc,
    };

    saveProfile(sanitizedParams);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialState}
      onSubmit={handleSubmit}
      validationSchema={OpenToSchema}
    >
      {(formik) => (
        <Form noValidate onSubmit={formik.handleSubmit}>
          <div className="section-container" id="Open-To">
            <div className="section-header mt-2">Open To</div>
            <div className="section-detail">
              <div className="section-detail-container">
                <div className="section-description">
                  <div>Opportunities you are interested in</div>
                </div>
              </div>
              <div className="hint">
                Are there specific things you are interested in getting from The Information
                subscriber community?
              </div>
              <div className="section-form-container">
                <div className="opens-to-container">
                  <CheckBoxField
                    name="investingInStartups"
                    label="Looking to invest in private companies"
                    disabled={isDisabled}
                  />
                  {formik.values.investingInStartups && (
                    <TextAreaField
                      name="investingInStartupsDesc"
                      rows={2}
                      placeholder="Describe startups you are interested in"
                      disabled={isDisabled}
                      maxCharCount={DESC_MAX_CHARACTER_LIMIT}
                    />
                  )}
                </div>
                <div className="opens-to-container">
                  <CheckBoxField
                    name="hiring"
                    label="Looking to hire team members"
                    disabled={isDisabled}
                  />
                  {formik.values.hiring && (
                    <TextAreaField
                      name="hiringDesc"
                      rows={2}
                      placeholder="Describe candidates you are looking for"
                      disabled={isDisabled}
                      maxCharCount={DESC_MAX_CHARACTER_LIMIT}
                    />
                  )}
                </div>
                <div className="opens-to-container">
                  <CheckBoxField
                    name="findingNewJob"
                    label="Looking for a job"
                    disabled={isDisabled}
                  />
                  {formik.values.findingNewJob && (
                    <TextAreaField
                      name="findingNewJobDesc"
                      rows={2}
                      placeholder="Describe ideal jobs you are looking for"
                      disabled={isDisabled}
                      maxCharCount={DESC_MAX_CHARACTER_LIMIT}
                    />
                  )}
                </div>
                <div className="opens-to-container">
                  <CheckBoxField
                    name="providingServices"
                    label="Looking to provide services"
                    disabled={isDisabled}
                  />
                  {formik.values.providingServices && (
                    <TextAreaField
                      name="providingServicesDesc"
                      rows={2}
                      placeholder="Describe the services you provide"
                      maxCharCount={DESC_MAX_CHARACTER_LIMIT}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="save-action-container">
              <Button
                className="profile-button --red --small-width"
                type="submit"
                disabled={!(formik.isValid && formik.dirty) || isDisabled || isSavingProfile}
              >
                {isSavingProfile ? 'Saving' : showSave ? 'Saved' : 'Save'}
              </Button>
            </div>
            <Divider />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default OpenTo;
